import { createSlice } from "@reduxjs/toolkit";
import type { CalendarRange } from "../../components/insights/calendar";

const WEEKS_PER_PAGE = 2; // group this many outer arrays into one pagination page

interface NewsFeedState {
  allNewsFeedData: any[][];
  paginationNewsFeedData: Record<string, any[][]>;
  topicFilter: string;
  priorityFilter: string[]; // UPDATED: was string, now string[]
  searchQuery: string;
  dateRange: CalendarRange;
  sortBy: '' | 'newest' | 'oldest' | 'priority-high' | 'priority-low';
  filteredNewsFeedData: any[][];
  feedMessage: string;
  feedLoading: boolean;
}

const initialState: NewsFeedState = {
  // list of lists (each inner list is a week's newsfeed)
  allNewsFeedData: [],

  // e.g. { "0": [ weekArray0, weekArray1 ], "1": [ weekArray2 ] }
  paginationNewsFeedData: {},

  // filters
  topicFilter: "all",
  priorityFilter: [], // UPDATED: was "all", now empty array means "all"
  searchQuery: "",
  dateRange: { from: undefined, to: undefined },
  sortBy: "",
  filteredNewsFeedData: [],
  feedMessage: "",
  feedLoading: false,
};

const newsFeedSlice = createSlice({
  name: "newsFeed",
  initialState,
  reducers: {
    // store the raw backend response (list of week-arrays)
    setAllNewsFeedData: (state, action) => {
      const response = Array.isArray(action.payload) ? action.payload : [];

      const processedData = response.map((weekArray, weekIndex) => {
        if (!Array.isArray(weekArray)) return [];
        return weekArray.map((item) => ({
          ...item,
          weekNo: weekIndex,
        }));
      });

      state.allNewsFeedData = processedData;
      // state.allNewsFeedData = action.payload;
    },

    // Build pagination mapping: pageIndex -> [ weekArray, weekArray, ... ]
    // Expects payload to be the same backend response: [ [wk0], [wk1], [wk2], ... ]
    setPaginationNewsFeedData: (state, action) => {
      const response = Array.isArray(action.payload) ? action.payload : [];
      const processedData = response.map((weekArray, weekIndex) => {
        if (!Array.isArray(weekArray)) return [];
        return weekArray.map((item) => ({
          ...item,
          weekNo: weekIndex,
        }));
      });

      const nonEmptyWeeks = processedData.filter(
        (weekArray) => Array.isArray(weekArray) && weekArray.length > 0
      );

      const paginationJson: Record<string, any[]> = {};

      for (let i = 0, page = 0; i < nonEmptyWeeks.length; i += WEEKS_PER_PAGE, page += 1) {
        const group = nonEmptyWeeks.slice(i, i + WEEKS_PER_PAGE);
        paginationJson[page.toString()] = group;
      }

      state.paginationNewsFeedData = paginationJson;
    },


    setTopicFilter: (state, action) => {
      state.topicFilter = action.payload;
    },

    // UPDATED: now accepts string[] instead of string
    setPriorityFilter: (state, action) => {
      state.priorityFilter = Array.isArray(action.payload) ? action.payload : [];
    },

    // NEW: toggle a single priority value in/out of the array
    togglePriorityFilter: (state, action: { payload: string }) => {
      const value = action.payload.toLowerCase();
      const idx = state.priorityFilter.indexOf(value);
      if (idx === -1) {
        state.priorityFilter.push(value);
      } else {
        state.priorityFilter.splice(idx, 1);
      }
    },

    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },

    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },

    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },


    setFilteredNewsFeedData: (state, action) => {
      const allData = Array.isArray(action.payload) ? action.payload : [];

      const topic = state.topicFilter.toLowerCase();
      const priorities = state.priorityFilter; // UPDATED: now an array
      const query = state.searchQuery.toLowerCase();
      const { from, to } = state.dateRange;
      // eslint-disable-next-line prefer-destructuring
      const sortBy = state.sortBy;

      const priorityOrderHighFirst: Record<string, number> = {
        high: 3,
        medium: 2,
        low: 1,
      };
      const priorityOrderLowFirst: Record<string, number> = {
        high: 1,
        medium: 2,
        low: 3,
      };

      const filtered = allData.map((weekArray) => {
        if (!Array.isArray(weekArray)) return [];

        // Preserve original order index
        const withIndex = weekArray.map((item, index) => ({ ...item, _originalIndex: index }));

        const filteredItems = withIndex.filter((item) => {
          const metricLabel = (item.base_metric || item.topic || "").toLowerCase();
          const matchesTopic =
            topic === "all" ||
            metricLabel.includes(topic);

          // UPDATED: empty array = no filter (show all); otherwise item must match one of the selected priorities
          const matchesPriority =
            priorities.length === 0 ||
            (item.priority && priorities.includes(item.priority.toLowerCase()));

          const matchesSearch =
            query === "" ||
            (item.heading && item.heading.toLowerCase().includes(query)) ||
            (item.summary && item.summary.toLowerCase().includes(query)) ||
            (item.base_metric && item.base_metric.toLowerCase().includes(query)) ||
            (item.topic && item.topic.toLowerCase().includes(query)) ||
            (item.description && item.description.toLowerCase().includes(query));

          const matchesDateRange = (() => {
            if (!from && !to) return true;
            if (!item.created_at) return false;
            const createdDate = new Date(item.created_at);
            if (Number.isNaN(createdDate.getTime())) return false;
            if (from && createdDate < new Date(from)) return false;
            if (to && createdDate > new Date(to)) return false;
            return true;
          })();

          return (
            matchesTopic && matchesPriority && matchesSearch && matchesDateRange
          );
        });

        const sortedItems = [...filteredItems];

        if (sortBy === "newest") {
          sortedItems.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        } else if (sortBy === "oldest") {
          sortedItems.sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        } else if (sortBy === "priority-high") {
          sortedItems.sort((a, b) => {
            const aPriority = priorityOrderHighFirst[a.priority?.toLowerCase()] || 0;
            const bPriority = priorityOrderHighFirst[b.priority?.toLowerCase()] || 0;
            return bPriority - aPriority;
          });
        } else if (sortBy === "priority-low") {
          sortedItems.sort((a, b) => {
            const aPriority = priorityOrderLowFirst[a.priority?.toLowerCase()] || 0;
            const bPriority = priorityOrderLowFirst[b.priority?.toLowerCase()] || 0;
            return bPriority - aPriority;
          });
        } else if (sortBy === "") {
          // reset to original unsorted order
          // eslint-disable-next-line no-underscore-dangle
          sortedItems.sort((a, b) => a._originalIndex - b._originalIndex);
        }

        // Clean up helper index before returning
        return sortedItems.map(({ _originalIndex, ...rest }) => rest);
      });

      state.filteredNewsFeedData = filtered;
    },

    setFeedMessage: (state, action) => {
      state.feedMessage = action.payload || "";
    },

    setFeedLoading: (state, action) => {
      state.feedLoading = Boolean(action.payload);
    },



    // optional: clear both
    clearNewsFeedData: (state) => {
      state.allNewsFeedData = [];
      state.paginationNewsFeedData = {};
      state.topicFilter = "all";
      state.priorityFilter = []; // UPDATED: was "all"
      state.searchQuery = "";
      state.dateRange = { from: undefined, to: undefined };
      state.sortBy = "";
      state.filteredNewsFeedData = [];
      state.feedMessage = "";
      state.feedLoading = false;
    },
  },
});

export const {
  setAllNewsFeedData,
  setPaginationNewsFeedData,
  setTopicFilter,
  setPriorityFilter,
  togglePriorityFilter,
  setSearchQuery,
  setDateRange,
  setSortBy,
  setFilteredNewsFeedData,
  setFeedMessage,
  setFeedLoading,
  clearNewsFeedData,
} = newsFeedSlice.actions;


export default newsFeedSlice.reducer;